import { useEffect, useMemo, useState } from 'react';
import { Modal, Form, Row, Col, Alert } from 'react-bootstrap';
import { Input } from '../../Input/Input';
import { TextWidth } from '@styled-icons/fa-solid';
import { postLicences } from '../../../api/compliance';
import NumberInput from '../../Input/NumberInput';

const LicenceModal = ({ show, licence, existingLicences, onHide, onSaved, onError }) => {
  const [localLicence, setLocalLicence] = useState({
    id: -1,
    documentCode: '',
    description: '',
    skillsMatrix: 10,
    disabled: false,
    createdBy: '',
    createdDate: '',
    lastUpdatedBy: '',
    lastUpdatedDate: '',
  });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [docError, setDocError] = useState(false);
  const [descError, setDescError] = useState(false);

  useEffect(() => {
    if (show) {
      setError('');
      setDocError(false);
      setDescError(false);
    }
  }, [show]);

  useEffect(() => {
    if (licence) {
      setLocalLicence({
        id: licence.id ?? -1,
        documentCode: licence.documentCode ?? '',
        description: licence.description ?? '',
        skillsMatrix: licence.skillsMatrix ?? 10,
        disabled: licence.disabled == 'Yes' ? true : false,
        createdBy: licence.createdBy ?? '',
        createdDate: licence.createdModalDate?.substring(0, 10) ?? '',
        lastUpdatedBy: licence.lastUpdatedBy ?? '',
        lastUpdatedDate: licence.lastUpdatedDate?.substring(0, 10) ?? '',
      });
    } else {
      setLocalLicence({
        id: -1,
        documentCode: '',
        description: '',
        skillsMatrix: 10,
        disabled: false,
        createdBy: '',
        createdDate: '',
        lastUpdatedBy: '',
        lastUpdatedDate: '',
      });
    }
  }, [licence]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setDocError(false);
    setDescError(false);

    let hasMissingFields = false;

    if (!localLicence.documentCode.trim()) {
      setDocError(true);
      hasMissingFields = true;
    }

    if (!localLicence.description.trim()) {
      setDescError(true);
      hasMissingFields = true;
    }

    if (hasMissingFields) {
      setError('Please complete all mandatory fields.');
      onError?.('Please complete all mandatory fields.');
      return;
    }

    // Map PascalCase from API/state to camelCase for backend
    const payload = {
      id: localLicence.id,
      documentCode: localLicence.documentCode,
      description: localLicence.description,
      skillsMatrix: localLicence.skillsMatrix ? Number(localLicence.skillsMatrix) : 10,
      disabled: localLicence.disabled,
      createdBy: localLicence.createdBy || '',
      createdDate: localLicence.createdDate || '',
      lastUpdatedBy: localLicence.lastUpdatedBy || '',
      lastUpdatedDate: localLicence.lastUpdatedDate || '',
    };

    try {
      setSaving(true);
      const isDuplicateResponse = (result) => Array.isArray(result) && result[0]?.message === 'Licence already exists';
      const result = await postLicences(payload);

      if (isDuplicateResponse(result)) {
        setError(result[0]?.message);
        onError?.(result[0]?.message);
        setDocError(true);
        setDescError(false);
        return;
      }

      if (!result?.[0]?.valid && result?.[0]?.message) {
        setError(result[0].message);
        onError?.(result[0].message);
        return;
      }

      if (payload.id === -1) {
        setDocError(false);
        setDescError(false);
        onSaved?.(true);
      } else {
        setDocError(false);
        setDescError(false);
        onSaved?.(false);
      }
    } catch (err) {
      console.error(err);
      setError('Error saving licence.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg" dialogClassName="wide-modal">
      <Modal.Header>
        <Modal.Title>
          {localLicence?.id === -1 ? 'Add Licence' : `Edit: ${localLicence?.documentCode || ''}`}
        </Modal.Title>
        <button type="button" className="btn-close" aria-label="Close" onClick={onHide} />
      </Modal.Header>
      <Form onSubmit={handleSubmit} className="flex-grow-1 d-flex flex-column">
        <Modal.Body className="flex-grow-1">
          {error && (
            <Alert variant="danger" className="mb-2">
              {error}
            </Alert>
          )}
          <Row className="mb-2">
            <Col>
              <Form.Group className="mb-2">
                <Form.Label>Document Code</Form.Label>
                <Form.Label style={{ color: 'red' }}>&nbsp;*</Form.Label>
              </Form.Group>
            </Col>
            <Col>
              <Form.Group className="mb-2">
                <Form.Control
                  name="documentCode"
                  type="text"
                  value={localLicence.documentCode}
                  onChange={(e) => setLocalLicence((prev) => ({ ...prev, documentCode: e.target.value }))}
                  isInvalid={docError}
                />
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-2">
            <Col>
              <Form.Group className="mb-2">
                <Form.Label>Description</Form.Label>
                <Form.Label style={{ color: 'red' }}>&nbsp;*</Form.Label>
              </Form.Group>
            </Col>
            <Col>
              <Form.Group className="mb-2">
                <Form.Control
                  type="text"
                  name="description"
                  value={localLicence.description}
                  onChange={(e) => setLocalLicence((prev) => ({ ...prev, description: e.target.value }))}
                  isInvalid={descError}
                />
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-2">
            <Col>
              <Form.Group className="mb-2">
                <Form.Label>Skills Matrix</Form.Label>
              </Form.Group>
            </Col>
            <Col>
              <Form.Group className="mb-2">
                <NumberInput
                  value={localLicence.skillsMatrix}
                  onChange={(newValue) => setLocalLicence((prev) => ({ ...prev, skillsMatrix: newValue }))}
                  min={10}
                  max={99}
                />
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-2 ">
            <Col>
              <Form.Group className="mb-2">
                <Form.Label>Disabled</Form.Label>
              </Form.Group>
            </Col>
            <Col>
              <Form.Group className="">
                <Form.Check
                  type="checkbox"
                  id="disabledCheckbox"
                  name="disabled"
                  checked={!!localLicence.disabled}
                  onChange={(e) => setLocalLicence((prev) => ({ ...prev, disabled: e.target.checked }))}
                  style={{ width: '1rem', height: '1.5rem' }}
                  className="d-flex align-items-center"
                />
              </Form.Group>
            </Col>
          </Row>
          {localLicence?.id !== -1 && (
            <Row className="mb-2">
              <Col>
                <Form.Group className="mb-2">
                  <Form.Label>Created By</Form.Label>
                </Form.Group>
              </Col>
              <Col>
                <Form.Group className="mb-2">
                  <p name="createdBy">{localLicence.createdBy}</p>
                </Form.Group>
              </Col>
            </Row>
          )}
          {localLicence?.id !== -1 && (
            <Row className="mb-2">
              <Col>
                <Form.Group className="mb-2">
                  <Form.Label>Created Date</Form.Label>
                </Form.Group>
              </Col>
              <Col>
                <Form.Group className="mb-2">
                  <p name="createdDate">{localLicence.createdDate}</p>
                </Form.Group>
              </Col>
            </Row>
          )}
          {localLicence?.id !== -1 && (
            <Row className="mb-2">
              <Col>
                <Form.Group className="mb-2">
                  <Form.Label>Last Updated By</Form.Label>
                </Form.Group>
              </Col>
              <Col>
                <Form.Group className="mb-2">
                  <p name="lastUpdatedBy">{localLicence.lastUpdatedBy}</p>
                </Form.Group>
              </Col>
            </Row>
          )}
          {localLicence?.id !== -1 && (
            <Row className="mb-2">
              <Col>
                <Form.Group className="mb-2">
                  <Form.Label>Last Updated Date</Form.Label>
                </Form.Group>
              </Col>
              <Col>
                <Form.Group className="mb-2">
                  <p name="lastUpdatedDate">{localLicence.lastUpdatedDate}</p>
                </Form.Group>
              </Col>
            </Row>
          )}
        </Modal.Body>
        <Modal.Footer
          className="d-flex flex-row py-3 px-3 gap-3 border-top bg-light justify-content-end align-items-center"
          style={{ borderBottomLeftRadius: '20px', borderBottomRightRadius: '20px' }}
        >
          <button
            type="button"
            className="cancel-button"
            onClick={onHide}
            style={{ display: 'flex', borderRadius: '10px' }}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="blue-button-teams green-btn"
            style={{ borderRadius: '10px' }}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default LicenceModal;
